import React, { useRef, useState } from 'react';
import { Upload, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

interface AccountData {
  email: string;
  password: string;
  orders: string;
  registration_date: string;
  sms_pin: string;
  phone_number: string;
  phone_expiry: string;
  backup_url: string;
  cookies_url: string;
}

const calculateReward = (orders: string): number => {
  const orderCount = parseInt(orders, 10);
  if (isNaN(orderCount)) return 0;

  if (orderCount > 500) return 1600;
  if (orderCount > 200) return 1400;
  if (orderCount > 50) return 1000;
  if (orderCount > 10) return 800;
  return 600;
};

const AccountUploader: React.FC = () => {
  const { user, refreshUser } = useAuth();
  const [uploadingAccount, setUploadingAccount] = useState(false);
  const [showAccountForm, setShowAccountForm] = useState(false);
  const [accountData, setAccountData] = useState<AccountData>({
    email: '',
    password: '',
    orders: '',
    registration_date: '',
    sms_pin: '',
    phone_number: '',
    phone_expiry: '',
    backup_url: '',
    cookies_url: ''
  });
  const backupFileRef = useRef<HTMLInputElement>(null);
  const cookiesFileRef = useRef<HTMLInputElement>(null);
  const [uploadingBackup, setUploadingBackup] = useState(false);
  const [uploadingCookies, setUploadingCookies] = useState(false);
  const [rpError, setRpError] = useState<string | null>(null);

  const handleBackupFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;

    setUploadingBackup(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random().toString(36).substring(2)}.${fileExt}`;
      const filePath = `${user?.id}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('backups')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('backups')
        .getPublicUrl(filePath);

      setAccountData(prev => ({ ...prev, backup_url: publicUrl }));
      toast.success('Backup file uploaded successfully');
    } catch (error) {
      console.error('Error uploading backup:', error);
      toast.error('Failed to upload backup file');
    } finally {
      setUploadingBackup(false);
    }
  };

  const handleCookiesFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;

    setUploadingCookies(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random().toString(36).substring(2)}.${fileExt}`;
      const filePath = `${user?.id}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('cookies')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('cookies')
        .getPublicUrl(filePath);

      setAccountData(prev => ({ ...prev, cookies_url: publicUrl }));
      toast.success('Cookies file uploaded successfully');
    } catch (error) {
      console.error('Error uploading cookies:', error);
      toast.error('Failed to upload cookies file');
    } finally {
      setUploadingCookies(false);
    }
  };

  const handleAccountSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    if (!accountData.backup_url || !accountData.cookies_url) {
      toast.error('Please upload both backup and cookies files');
      return;
    }

    setUploadingAccount(true);
    setRpError(null);
    
    try {
      const reward = calculateReward(accountData.orders);

      // First create the account
      const { data: account, error: accountError } = await supabase
        .from('accounts')
        .insert([{
          ...accountData,
          created_by: user.id,
          // No status field - will use default 'pending'
        }])
        .select()
        .single();

      if (accountError) throw accountError;

      // Update user's balance
      const { error: balanceError } = await supabase
        .from('users')
        .update({ 
          balance: user.balance + reward 
        })
        .eq('id', user.id);

      if (balanceError) throw balanceError;

      // Log the actions
      await supabase.from('system_logs').insert([
        {
          user_id: user.id,
          action: 'CREATE_ACCOUNT',
          details: `Created account for ${accountData.email}`
        },
        {
          user_id: user.id,
          action: 'BALANCE_UPDATE',
          details: `Added ₽${reward} for account ${accountData.email} (${accountData.orders} orders)`
        }
      ]);

      // Check if RP was awarded by looking for recent RP transactions
      const { data: rpTransactions, error: rpError } = await supabase
        .from('reward_transactions')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(1);

      if (rpError) {
        console.error('Error checking RP transactions:', rpError);
        setRpError('Failed to verify RP award. Please check your RP balance manually.');
      } else if (!rpTransactions || rpTransactions.length === 0) {
        // No RP transaction found
        setRpError('No RP was awarded. This might be due to a system issue.');
        
        // Log this issue
        await supabase.from('system_logs').insert({
          user_id: user.id,
          action: 'RP_AWARD_FAILED',
          details: `No RP transaction found after account creation: ${accountData.email}`
        });
      } else {
        // RP was awarded successfully
        const rpTransaction = rpTransactions[0];
        toast.success(`Earned ${rpTransaction.amount} RP! (${rpTransaction.description})`);
      }

      // Check user rewards record
      const { data: userRewards, error: userRewardsError } = await supabase
        .from('user_rewards')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (userRewardsError && !userRewardsError.message.includes('No rows found')) {
        console.error('Error checking user rewards:', userRewardsError);
        setRpError((prev) => prev ? `${prev}\nFailed to verify user rewards record.` : 'Failed to verify user rewards record.');
      } else if (!userRewards) {
        // No user rewards record found
        setRpError((prev) => prev ? `${prev}\nNo user rewards record found.` : 'No user rewards record found.');
        
        // Create a user rewards record
        const { error: createRewardsError } = await supabase
          .from('user_rewards')
          .insert({
            user_id: user.id,
            rp_balance: 0,
            quota_reduction: 0
          });
          
        if (createRewardsError) {
          console.error('Error creating user rewards record:', createRewardsError);
          setRpError((prev) => prev ? `${prev}\nFailed to create user rewards record.` : 'Failed to create user rewards record.');
        } else {
          toast.success('Created user rewards record. Future RP awards should work correctly.');
        }
      }

      await refreshUser();

      toast.success(`Account uploaded successfully. Added ₽${reward} to your balance!`);
      setAccountData({
        email: '',
        password: '',
        orders: '',
        registration_date: '',
        sms_pin: '',
        phone_number: '',
        phone_expiry: '',
        backup_url: '',
        cookies_url: ''
      });
      setShowAccountForm(false);
    } catch (error: any) {
      console.error('Error uploading account:', error);
      toast.error(`Failed to upload account: ${error.message || 'Unknown error'}`);
      
      // Log the error
      await supabase.from('system_logs').insert({
        user_id: user?.id,
        action: 'ACCOUNT_UPLOAD_ERROR',
        details: `Error uploading account: ${error.message || 'Unknown error'}`
      });
    } finally {
      setUploadingAccount(false);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
      <div className="px-4 py-5 sm:p-6">
        <div className="flex items-center mb-4">
          <Upload className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
          <h3 className="ml-3 text-lg font-medium text-gray-900 dark:text-white">Upload Account</h3>
        </div>
        {!showAccountForm ? (
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Upload a new account with required information
            </p>
            <button
              onClick={() => setShowAccountForm(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600"
            >
              <Upload className="h-5 w-5 mr-2" />
              Start Upload
            </button>
          </div>
        ) : (
          <form onSubmit={handleAccountSubmit} className="space-y-4">
            {rpError && (
              <div className="bg-yellow-50 dark:bg-yellow-900 border-l-4 border-yellow-400 p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <AlertTriangle className="h-5 w-5 text-yellow-400" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-yellow-700 dark:text-yellow-200">
                      {rpError}
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  required
                  value={accountData.email}
                  onChange={(e) => setAccountData(prev => ({ ...prev, email: e.target.value }))}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Password
                </label>
                <input
                  type="text"
                  id="password"
                  required
                  value={accountData.password}
                  onChange={(e) => setAccountData(prev => ({ ...prev, password: e.target.value }))}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
              </div>

              <div>
                <label htmlFor="orders" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Orders
                </label>
                <input
                  type="text"
                  id="orders"
                  value={accountData.orders}
                  onChange={(e) => setAccountData(prev => ({ ...prev, orders: e.target.value }))}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
              </div>

              <div>
                <label htmlFor="registration_date" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Registration Date
                </label>
                <input
                  type="text"
                  id="registration_date"
                  value={accountData.registration_date}
                  onChange={(e) => setAccountData(prev => ({ ...prev, registration_date: e.target.value }))}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
              </div>

              <div>
                <label htmlFor="sms_pin" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  SMS PIN
                </label>
                <input
                  type="text"
                  id="sms_pin"
                  value={accountData.sms_pin}
                  onChange={(e) => setAccountData(prev => ({ ...prev, sms_pin: e.target.value }))}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
              </div>

              <div>
                <label htmlFor="phone_number" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Phone Number
                </label>
                <input
                  type="text"
                  id="phone_number"
                  value={accountData.phone_number}
                  onChange={(e) => setAccountData(prev => ({ ...prev, phone_number: e.target.value }))}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
              </div>

              <div>
                <label htmlFor="phone_expiry" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Phone Expiry
                </label>
                <input
                  type="text"
                  id="phone_expiry"
                  value={accountData.phone_expiry}
                  onChange={(e) => setAccountData(prev => ({ ...prev, phone_expiry: e.target.value }))}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Backup File
                  </label>
                  <input
                    type="file"
                    ref={backupFileRef}
                    onChange={handleBackupFileChange}
                    className="hidden"
                    accept=".zip,.rar,.7zip"
                  />
                  <div className="mt-1 flex items-center space-x-2">
                    <button
                      type="button"
                      onClick={() => backupFileRef.current?.click()}
                      disabled={uploadingBackup}
                      className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white ${
                        uploadingBackup
                          ? 'bg-indigo-400 dark:bg-indigo-400'
                          : 'bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600'
                      }`}
                    >
                      {uploadingBackup ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Upload className="h-5 w-5 mr-2" />
                          Upload Backup
                        </>
                      )}
                    </button>
                    {accountData.backup_url && (
                      <span className="text-sm text-green-600 dark:text-green-400">✓ Uploaded</span>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Cookies File
                  </label>
                  <input
                    type="file"
                    ref={cookiesFileRef}
                    onChange={handleCookiesFileChange}
                    className="hidden"
                    accept=".txt,.json"
                  />
                  <div className="mt-1 flex items-center space-x-2">
                    <button
                      type="button"
                      onClick={() => cookiesFileRef.current?.click()}
                      disabled={uploadingCookies}
                      className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white ${
                        uploadingCookies
                          ? 'bg-indigo-400 dark:bg-indigo-400'
                          : 'bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600'
                      }`}
                    >
                      {uploadingCookies ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Upload className="h-5 w-5 mr-2" />
                          Upload Cookies
                        </>
                      )}
                    </button>
                    {accountData.cookies_url && (
                      <span className="text-sm text-green-600 dark:text-green-400">✓ Uploaded</span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex space-x-2">
              <button
                type="submit"
                disabled={uploadingAccount || !accountData.backup_url || !accountData.cookies_url}
                className={`flex-1 inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white ${
                  uploadingAccount || !accountData.backup_url || !accountData.cookies_url
                    ? 'bg-indigo-400 dark:bg-indigo-400'
                    : 'bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600'
                } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500`}
              >
                {uploadingAccount ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="h-5 w-5 mr-2" />
                    Upload Account
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowAccountForm(false);
                  setRpError(null);
                }}
                className="inline-flex justify-center items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default AccountUploader;